import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Params, Route, Router } from '@angular/router';
import { FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { TranslocoService } from '@jsverse/transloco';
import { MainComponent } from '@project/main.component';
import { SharedModule } from 'app/modules/shared/shared.module';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { HeaderService } from './header.service';

interface Breadcrumb {
    label: string;
    url: string;
    queryParams?: Params;
    translationParams?: unknown;
    queryParamConfig?: BreadcrumbQueryParam;
}

/**
 * Query param che identifica la vista (le liste filtrate dalle card della dashboard vivono
 * sulla stessa rotta della lista completa): la breadcrumb lo ricorda per tornare alla lista
 * filtrata e ne ricava l'etichetta
 */
interface BreadcrumbQueryParam {
    param: string;
    labels: Record<string, string>;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',

    encapsulation: ViewEncapsulation.None,
    imports: [CommonModule, SharedModule],
})
export class HeaderComponent extends MainComponent implements OnInit, OnDestroy {
    headerText?: string;
    subHeaderText?: string;
    breadcrumbs: Breadcrumb[] = [];
    bg: string;
    hidden = false;
    hideBreadcrumbs = false;

    headerContentTemplate?: TemplateRef<HTMLElement>;
    private subscriptions = new Subscription();

    /**
     * Ultimi query param visti per ogni path: le breadcrumb riportano alla lista com'era
     * (filtro arrivato dalla dashboard incluso), non alla sua versione senza parametri
     */
    private lastQueryParamsByPath = new Map<string, Params>();

    queryParams: Params;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private headerService: HeaderService,
        private _translocoService: TranslocoService,
        private _fuseNavigationService: FuseNavigationService
    ) {
        super();
    }

    ngOnInit(): void {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            this.executeEvent();
        });

        // Sottoscrizione al contenuto dell'header
        this.subscriptions.add(
            this.headerService.headerContent$.subscribe((template) => {
                this.headerContentTemplate = template;
            })
        );

        // Sottoscrizione per nascondere le breadcrumbs
        this.subscriptions.add(
            this.headerService.hideBreadcrumbs$.subscribe((hide) => {
                this.hideBreadcrumbs = hide;
            })
        );

        this.executeEvent();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    private executeEvent(): void {
        this.breadcrumbs = [];
        let currentRoute = this.route.root;
        let url = '';

        while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
            const routeSnapshot = currentRoute.snapshot;
            const routeConfig = currentRoute.routeConfig;

            if (routeConfig && routeConfig.path) {
                // Gestione dei parametri di rotta
                const path = this.replaceRouteParams(routeConfig.path, routeSnapshot.params);
                url += `/${path}`;

                // Ottenimento dell'etichetta della breadcrumb
                const label = this.getBreadcrumbLabel(routeConfig, routeSnapshot.params);

                if (label) {
                    const queryParamConfig = routeConfig.data?.['breadcrumbQueryParam'] as BreadcrumbQueryParam | undefined;
                    this.breadcrumbs.push({ label, url, queryParamConfig });
                }
            }
        }

        // Recupera i query params
        const queryParams = this.route.snapshot.queryParams;
        this.queryParams = queryParams;

        // Memorizza il query param della pagina corrente per il ritorno dalle pagine figlie.
        // Solo quello dichiarato dalla rotta: gli altri (tab, openModal…) hanno effetti
        // collaterali e riproporli aprirebbe di nuovo tab o modali
        const declaredParam = this.breadcrumbs.find((breadcrumb) => breadcrumb.url === url)?.queryParamConfig?.param;
        if (declaredParam && queryParams[declaredParam]) {
            this.lastQueryParamsByPath.set(url, { [declaredParam]: queryParams[declaredParam] });
        } else {
            this.lastQueryParamsByPath.delete(url);
        }

        // Aggiorna i translationParams delle breadcrumbs
        this.breadcrumbs = this.breadcrumbs.map((breadcrumb) => {
            // Unisci i translationParams della rotta con i query params
            const translationParams = {
                ...queryParams,
            };
            const breadcrumbQueryParams = this.lastQueryParamsByPath.get(breadcrumb.url);
            return {
                ...breadcrumb,
                label: this.resolveBreadcrumbLabel(breadcrumb, breadcrumbQueryParams),
                queryParams: breadcrumbQueryParams,
                translationParams,
            };
        });

        // Impostazione del testo dell'header
        const data = currentRoute.snapshot.data;
        this.headerText = data['headerText'];
        this.subHeaderText = data['subHeaderText'];
        this.bg = data['bg'];

        // Aggiunta l gestione hidden
        this.hidden = data['hidden'] || false;

        // Nascondi la breadcrumb se headerText non è definito
        if (!this.headerText) {
            this.breadcrumbs = [];
        }
    }

    get isKioskMode(): boolean {
        return localStorage.getItem('kioskMode') === 'true';
    }

    private replaceRouteParams(path: string, params: Params): string {
        // eslint-disable-next-line no-useless-escape
        return path.replace(/:([^\/]+)/g, (_, key) => params[key]);
    }

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation = this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(name);

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Sulle liste filtrate la breadcrumb prende il nome della card della dashboard da cui
     * si arriva, al posto di quello generico della sezione
     */
    private resolveBreadcrumbLabel(breadcrumb: Breadcrumb, queryParams: Params | undefined): string {
        const config = breadcrumb.queryParamConfig;
        if (!config || !queryParams) {
            return breadcrumb.label;
        }
        return config.labels[queryParams[config.param]] ?? breadcrumb.label;
    }

    private getBreadcrumbLabel(routeConfig: Route, params: Params): string {
        let label = routeConfig.data ? routeConfig.data['breadcrumb'] : '';
        // Sostituzione dei parametri nella label
        if (label) {
            label = this.replaceRouteParams(label, params);
        }
        return label;
    }
}
